import React, { useEffect, useState } from 'react';
import { accountAPI } from '../services/api';

const ABookPage: React.FC = () => {
  const [aBookStates, setABookStates] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [returning, setReturning] = useState<string | null>(null);

  const fetchData = async () => {
    try {
      const res = await accountAPI.getABook();
      setABookStates(res.data);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
    const interval = setInterval(fetchData, 10000);
    return () => clearInterval(interval);
  }, []);

  const handleReturn = async (accountId: string) => {
    if (!window.confirm(`계좌 ${accountId}를 B북으로 복귀시키겠습니까?`)) return;
    setReturning(accountId);
    try {
      await accountAPI.returnToBBook(accountId);
      await fetchData();
    } catch {
      alert('복귀 처리 중 오류가 발생했습니다.');
    } finally {
      setReturning(null);
    }
  };

  const totalDrawdown = aBookStates.reduce((sum, s) => sum + (s.triggerDrawdown || 0), 0);
  const avgDrawdown = aBookStates.length > 0 ? totalDrawdown / aBookStates.length : 0;

  return (
    <div style={styles.container}>
      <div style={styles.header}>
        <div>
          <h1 style={styles.title}>🔴 A북 현황</h1>
          <p style={styles.subtitle}>10초마다 자동 갱신</p>
        </div>
        <button style={styles.refreshBtn} onClick={fetchData}>🔄 새로고침</button>
      </div>

      {/* 요약 */}
      <div style={styles.summaryGrid}>
        <div style={styles.summaryCard}>
          <div style={styles.summaryLabel}>현재 A북 계좌</div>
          <div style={{ ...styles.summaryValue, color: '#ff6b6b' }}>{aBookStates.length}</div>
        </div>
        <div style={styles.summaryCard}>
          <div style={styles.summaryLabel}>평균 Drawdown</div>
          <div style={{ ...styles.summaryValue, color: '#f7971e' }}>{avgDrawdown.toFixed(2)}%</div>
        </div>
        <div style={styles.summaryCard}>
          <div style={styles.summaryLabel}>최대 Drawdown</div>
          <div style={{ ...styles.summaryValue, color: '#ff6b6b' }}>
            {aBookStates.length > 0 ? Math.min(...aBookStates.map(s => s.triggerDrawdown || 0)).toFixed(2) : 0}%
          </div>
        </div>
      </div>

      {/* 계좌 목록 */}
      {loading ? (
        <div style={styles.loading}>로딩 중...</div>
      ) : aBookStates.length === 0 ? (
        <div style={styles.emptyState}>
          <div style={styles.emptyIcon}>✅</div>
          <div style={styles.emptyText}>현재 A북 전환된 계좌가 없습니다.</div>
          <div style={styles.emptySub}>모든 계좌가 B북으로 정상 운영 중입니다.</div>
        </div>
      ) : (
        <div style={styles.cardGrid}>
          {aBookStates.map((state: any) => (
            <div key={state.id} style={styles.accountCard}>
              <div style={styles.cardHeader}>
                <span style={styles.accountId}>{state.accountId}</span>
                <span style={styles.aBookBadge}>A북</span>
              </div>
              <div style={styles.cardBody}>
                <div style={styles.infoRow}>
                  <span style={styles.infoLabel}>심볼</span>
                  <span style={styles.infoValue}>{state.symbol}</span>
                </div>
                <div style={styles.infoRow}>
                  <span style={styles.infoLabel}>Drawdown</span>
                  <span style={{ ...styles.infoValue, color: '#ff6b6b', fontWeight: 700 }}>
                    {state.triggerDrawdown?.toFixed(2)}%
                  </span>
                </div>
                <div style={styles.infoRow}>
                  <span style={styles.infoLabel}>전환 Equity</span>
                  <span style={styles.infoValue}>${state.triggerEquity?.toLocaleString()}</span>
                </div>
                <div style={styles.infoRow}>
                  <span style={styles.infoLabel}>전환 Balance</span>
                  <span style={styles.infoValue}>${state.triggerBalance?.toLocaleString()}</span>
                </div>
                <div style={styles.infoRow}>
                  <span style={styles.infoLabel}>전환 시각</span>
                  <span style={styles.infoValue}>
                    {state.triggeredAt ? new Date(state.triggeredAt).toLocaleString('ko-KR') : '-'}
                  </span>
                </div>
              </div>
              <button
                style={returning === state.accountId ? { ...styles.returnBtn, opacity: 0.6 } : styles.returnBtn}
                onClick={() => handleReturn(state.accountId)}
                disabled={returning === state.accountId}
              >
                {returning === state.accountId ? '처리 중...' : '🔄 B북 복귀'}
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

const styles: Record<string, React.CSSProperties> = {
  container: { color: '#fff', maxWidth: '1200px' },
  header: { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '24px' },
  title: { fontSize: '28px', fontWeight: 700, color: '#fff', margin: '0 0 4px' },
  subtitle: { color: 'rgba(255,255,255,0.4)', fontSize: '13px', margin: 0 },
  refreshBtn: { background: 'rgba(255,255,255,0.08)', border: '1px solid rgba(255,255,255,0.15)', borderRadius: '8px', padding: '8px 16px', color: 'rgba(255,255,255,0.7)', fontSize: '13px', cursor: 'pointer' },
  summaryGrid: { display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '16px', marginBottom: '32px' },
  summaryCard: { background: 'rgba(255,107,107,0.08)', border: '1px solid rgba(255,107,107,0.2)', borderRadius: '16px', padding: '24px', textAlign: 'center' },
  summaryLabel: { color: 'rgba(255,255,255,0.5)', fontSize: '13px', marginBottom: '8px' },
  summaryValue: { fontSize: '36px', fontWeight: 700 },
  cardGrid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))', gap: '16px' },
  accountCard: { background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,107,107,0.3)', borderRadius: '16px', padding: '20px', display: 'flex', flexDirection: 'column', gap: '16px' },
  cardHeader: { display: 'flex', justifyContent: 'space-between', alignItems: 'center' },
  accountId: { fontFamily: 'monospace', fontSize: '16px', fontWeight: 700, color: '#667eea' },
  aBookBadge: { background: 'rgba(255,107,107,0.2)', color: '#ff6b6b', padding: '2px 10px', borderRadius: '20px', fontSize: '12px', fontWeight: 700 },
  cardBody: { display: 'flex', flexDirection: 'column', gap: '8px' },
  infoRow: { display: 'flex', justifyContent: 'space-between', alignItems: 'center' },
  infoLabel: { color: 'rgba(255,255,255,0.4)', fontSize: '13px' },
  infoValue: { color: 'rgba(255,255,255,0.9)', fontSize: '13px', fontWeight: 500 },
  returnBtn: { background: 'rgba(0,210,255,0.15)', border: '1px solid rgba(0,210,255,0.3)', borderRadius: '10px', padding: '10px', color: '#00d2ff', fontSize: '14px', cursor: 'pointer', fontWeight: 600 },
  emptyState: { textAlign: 'center', padding: '80px 40px' },
  emptyIcon: { fontSize: '64px', marginBottom: '16px' },
  emptyText: { color: '#fff', fontSize: '18px', fontWeight: 600, marginBottom: '8px' },
  emptySub: { color: 'rgba(255,255,255,0.4)', fontSize: '14px' },
  loading: { padding: '40px', textAlign: 'center', color: 'rgba(255,255,255,0.4)' },
};

export default ABookPage;
